<template>
  <div>
    <v-dialog v-model="isOpen" width="600" persistent>
      <v-card>
        <v-card-title class="d-flex align-center justify-space-between">
          <h1>{{ dialogTitle }}</h1>
          <v-btn icon @click="close()" class="align-self-end">
            <v-icon> mdi-close </v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <div v-if="isNegociationLoading" class="d-flex justify-center py-12">
            <v-progress-circular color="primary" indeterminate class="py-12" />
          </div>
          <v-form v-else :key="forceReset">
            <v-row>
              <v-col cols="12" class="py-4">
                <v-autocomplete
                  v-model="transhipmentId"
                  label="Transbordo"
                  :items="transhipmentItems"
                  item-text="name"
                  item-value="id"
                  :disabled="isEdit"
                  no-data-text="Nenhum transbordo disponível"
                >
                </v-autocomplete>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="6" class="pt-0">
                <v-radio-group v-model="selectedType" column hide-details>
                  <template #label> Hierarquia: </template>
                  <v-radio v-for="{ id, name } in negotiationTypes" :key="id" :label="name" :value="id"></v-radio>
                </v-radio-group>
              </v-col>
              <v-col cols="1" class="pt-0 mx-0">
                <v-divider vertical></v-divider>
              </v-col>
              <v-col cols="5" class="pt-0 mx-0">
                <v-radio-group v-model="selectedSubType" column hide-details>
                  <template #label> Tipo: </template>
                  <v-radio v-for="{ id, name } in negotiationSubTypes" :key="id" :label="name" :value="id"></v-radio>
                </v-radio-group>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions class="d-flex justify-center pb-4">
          <v-btn @click="close()" color="error" class="flex-grow-1"> CANCELAR </v-btn>
          <v-btn
            @click="onSubmitConfirm()"
            :loading="isButtonLoading"
            color="success"
            class="flex-grow-1"
            :disabled="!transhipmentId"
          >
            SALVAR
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <MessagesError :errors="errors" @closeMessages="errors = []" :type="errorsType" />
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import { VMoney } from 'v-money';
import MessagesError from '@/components/MessagesError.vue';
import { orderBy } from 'lodash';
export default {
  components: {
    MessagesError
  },

  props: {
    isOpen: {
      type: Boolean,
      default: false
    },

    negociationToEdit: {
      type: Object,
      default: null
    }
  },

  directives: { money: VMoney },

  data: () => ({
    negotiationTypes: [],
    negotiationSubTypes: [],
    isButtonLoading: false,
    isNegociationLoading: false,
    forceReset: 0,
    transhipments: [],
    transhipmentId: null,
    selectedType: 'cte',
    selectedSubType: null,
    errors: [],
    errorsType: 'error',
    value: null,
    loadingCity: false,
    loadingToCreate: false,
    types: [
      { name: 'Município', id: 1 },
      { name: 'CNPJ', id: 0 }
    ],
    type: 1,
    stateId: '',
    cityId: '',
    cpfOrCnpj: null
  }),

  computed: {
    isEdit() {
      return this.negociationToEdit !== null;
    },

    dialogTitle() {
      return `${this.isEdit ? 'Editar' : 'Nova'} negociação`;
    },
    transhipmentItems() {
      return this.isEdit ? [this.negociationToEdit] : this.transhipments;
    }
  },

  watch: {
    isOpen: {
      handler(isOpen) {
        if (isOpen) {
          this.fetchAllNegociations();
          this.resetForm();
        }
      }
    }
  },

  created() {
    this.fetchAllNegociations();
    this.fetchNegotiationTypes();
    this.fetchNegotiationSubTypes();
  },

  methods: {
    ...mapActions('AdressStore', ['fetchStates', 'fetchCities']),

    ...mapActions('TranshipmentStore', [
      'getTranshipmentsWithoutNegotiation',
      'getNegotiationTypes',
      'getNegotiationSubTypes',
      'createNegotiation',
      'editNegotiation'
    ]),

    onSubmitConfirm() {
      return this.isEdit ? this.handleEditNegotiation() : this.handleCreateNegotiation();
    },

    async handleCreateNegotiation() {
      this.isButtonLoading = true;

      let params = {
        transhipmentId: this.transhipmentId,
        typeNegotiationId: this.selectedType,
        subTypeNegotiationId: this.selectedSubType
      };

      try {
        const status = await this.createNegotiation(params);

        if (status.success) {
          this.errorsType = 'success';
          this.errors = ['Negociação criada com sucesso!'];
          this.$emit('shouldRefresh');
          this.close();
        } else {
          this.errorsType = 'error';
          this.errors = status.errors;
        }
      } catch (err) {
        console.error(err);
      } finally {
        this.isButtonLoading = false;
      }
    },

    async handleEditNegotiation() {
      this.isButtonLoading = true;

      let params = {
        id: this.negociationToEdit.id,
        typeNegotiationId: this.selectedType,
        subTypeNegotiationId: this.selectedSubType
      };

      try {
        const status = await this.editNegotiation(params);
        if (status.success) {
          this.errorsType = 'success';
          this.errors = ['Negociação editada com sucesso!'];
          this.$emit('shouldRefresh');
          this.close();
        } else {
          this.errorsType = 'error';
          this.errors = status.errors;
        }
      } catch (err) {
        console.error(err);
      } finally {
        this.isButtonLoading = false;
      }
    },

    async fetchAllNegociations() {
      try {
        this.transhipments = await this.getTranshipmentsWithoutNegotiation();
      } catch (e) {
        console.error(e);
      }
    },

    async fetchNegotiationTypes() {
      try {
        const types = await this.getNegotiationTypes();
        this.negotiationTypes = orderBy(types, ['name'], ['desc']);
        const [stopType] = this.negotiationTypes;
        this.selectedType = stopType.id;
      } catch (error) {
        console.error(error);
      }
    },

    async fetchNegotiationSubTypes() {
      try {
        const subTypes = await this.getNegotiationSubTypes();
        this.negotiationSubTypes = orderBy(subTypes, ['name'], ['desc']);
        const [stopType] = this.negotiationSubTypes;
        this.selectedSubType = stopType.id;
      } catch (error) {
        console.error(error);
      }
    },

    resetForm() {
      if (this.isEdit) {
        const { id, typeNegotiation, subTypeNegotiation } = this.negociationToEdit;
        this.transhipmentId = id;

        const types = {};
        const subTypes = {};
        this.negotiationTypes.forEach((type) => {
          types[type.name] = type.id;
        });
        this.negotiationSubTypes.forEach((type) => {
          subTypes[type.name] = type.id;
        });

        this.selectedType = types[typeNegotiation];
        this.selectedSubType = subTypes[subTypeNegotiation];
      } else {
        this.transhipmentId = null;
        const [stopType] = this.negotiationTypes;
        this.selectedType = stopType.id;
      }
    },

    close() {
      this.$emit('update:isOpen', false);
    }
  }
};
</script>

<style lang="scss" scoped>
::v-deep.v-input__slot {
  margin: 0;
  padding: 0;

  &::after,
  &::before {
    display: none !important;
  }
}
</style>
